/**
 * Sezione "Forniture" nel dettaglio cliente: tutte le consegne al cliente,
 * raggruppate per data (desc). Ogni gruppo ("deployment") contiene 1 o più
 * release prodotto consegnate in quella data.
 *
 * Dati: GET /customers/:id/deployments (tutti i prodotti). I nomi prodotto
 * sono risolti lato client dalla lista prodotti (useProducts).
 */

import { useMemo } from 'react';
import { useCustomerDeployments } from './hooks.js';
import { useProducts } from '../products/hooks.js';
import type { Deployment, DeploymentGroup } from './types.js';
import styles from './Customers.module.css';

/** Formatta una data ISO (YYYY-MM-DD o full ISO) in formato leggibile it. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/**
 * Raggruppa le consegne per deliveredAt (giorno). L'array di partenza è già
 * ordinato per data desc dal backend; il raggruppamento preserva l'ordine.
 */
function groupByDate(deployments: Deployment[]): DeploymentGroup[] {
  const groups: DeploymentGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const dep of deployments) {
    const key = dep.deliveredAt.slice(0, 10);
    const idx = indexByKey.get(key);
    if (idx === undefined) {
      indexByKey.set(key, groups.length);
      groups.push({ deliveredAt: key, deployments: [dep] });
    } else {
      groups[idx]?.deployments.push(dep);
    }
  }
  // Safety: ordina per data desc (il backend già ordina, ma difensivo).
  groups.sort((a, b) => (a.deliveredAt < b.deliveredAt ? 1 : -1));
  return groups;
}

/** Riga di una singola release prodotto consegnata. */
function ReleaseRow({ dep, productName }: { dep: Deployment; productName: string }) {
  return (
    <tr data-testid={`fornitura-row-${dep.id}`}>
      <td>{productName}</td>
      <td>
        <span className={styles.badge}>{dep.releaseVersion}</span>
      </td>
      <td>{dep.label ?? '—'}</td>
      <td>{dep.siteId ?? '—'}</td>
      <td>
        {dep.isCurrent ? (
          <span className={styles.badge}>corrente</span>
        ) : (
          <span style={{ color: '#999' }}>no</span>
        )}
      </td>
    </tr>
  );
}

/** Un gruppo di consegne (stessa data) = un "deployment". */
function DeploymentGroupCard({ group }: { group: DeploymentGroup }) {
  const { data: products = [] } = useProducts();
  const productNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of products) m.set(p.internalId, p.name);
    return m;
  }, [products]);

  return (
    <div className={styles.deploymentGroup} data-testid={`deployment-group-${group.deliveredAt}`}>
      <p className={styles.deploymentDate} data-testid={`deployment-date-${group.deliveredAt}`}>
        {formatDate(group.deliveredAt)}
        <span className={styles.deploymentCount}>
          {' '}
          ({group.deployments.length} {group.deployments.length === 1 ? 'release' : 'release'})
        </span>
      </p>
      <table className={styles.table} data-testid={`deployment-table-${group.deliveredAt}`}>
        <thead>
          <tr>
            <th>Prodotto</th>
            <th>Release</th>
            <th>Label</th>
            <th>Sito</th>
            <th>Stato</th>
          </tr>
        </thead>
        <tbody>
          {group.deployments.map((dep) => (
            <ReleaseRow
              key={dep.id}
              dep={dep}
              productName={productNames.get(dep.productInternalId) ?? dep.productInternalId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Sezione "Forniture" del dettaglio cliente. */
export function CustomerDeploymentsSection({ customerId }: { customerId: number }) {
  const { data: deployments, isLoading, isError, error } = useCustomerDeployments(customerId);

  const groups = useMemo(() => groupByDate(deployments ?? []), [deployments]);

  return (
    <div data-testid="sezione-forniture-cliente">
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-forniture-cliente">
          Caricamento forniture...
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-forniture-cliente">
          {error instanceof Error ? error.message : 'Errore nel caricamento delle forniture'}
        </p>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <div className={styles.emptyState} data-testid="forniture-cliente-vuote">
          Nessuna consegna registrata per questo cliente.
        </div>
      )}

      {!isLoading && !isError && groups.length > 0 && (
        <div data-testid="lista-deployments-cliente">
          {groups.map((g) => (
            <DeploymentGroupCard key={g.deliveredAt} group={g} />
          ))}
        </div>
      )}
    </div>
  );
}
